<template>
  <div id="app">
    <div class="container">
      <!-- Lista de pareceres cardiológicos do paciente -->
      <b-card v-if="!showParecerCardiologicoForm && !loading">
        <b-card-body>
          <div v-if="pareceres.length === 0" class="text-center py-4">
            <feather-icon
              icon="FileTextIcon"
              size="48"
              class="text-muted mb-2"
            />
            <p class="text-muted">
              Nenhum parecer cardiológico encontrado para este paciente.
            </p>
            <b-button variant="primary" @click="createParecerCardiologico">
              Criar Primeiro Parecer Cardiológico
            </b-button>
          </div>

          <b-list-group v-else>
            <div class="justify-content-end d-flex mb-2 flex-wrap">
              <b-button variant="primary" @click="createParecerCardiologico">
                Criar Novo Parecer Cardiológico
              </b-button>
            </div>
            <b-list-group-item
              v-for="parecer in pareceres"
              :key="parecer.id"
              class="d-flex justify-content-between align-items-center"
            >
              <div>
                <h6 class="mb-1">Parecer Cardiológico #{{ parecer.id }}</h6>
                <small>
                  Data: {{ formatHyphenToSlash(parecer.dataParecer) }}
                </small>
              </div>
              <div>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="mr-1"
                  title="Editar Parecer Cardiológico"
                  @click="editParecerCardiologico(parecer)"
                >
                  <feather-icon icon="EditIcon" />
                </b-button>
              </div>
            </b-list-group-item>
          </b-list-group>
        </b-card-body>
      </b-card>

      <!-- Loading spinner -->
      <div v-if="loading" class="text-center py-4">
        <b-spinner variant="primary" label="Carregando..." />
        <p class="mt-2 text-muted">Carregando pareceres cardiológicos...</p>
      </div>

      <!-- Formulário de odontograma (será mostrado quando showOdontogramaForm = true) -->
      <div
        v-if="showParecerCardiologicoForm"
        class="justify-content-end d-flex flex-wrap"
      >
        <b-button
          class="mb-3"
          variant="outline-secondary"
          @click="backToList()"
        >
          <feather-icon icon="ArrowLeftIcon" />
          Voltar para Lista
        </b-button>
        <DigitalParecerCardiologico
          class="col-12"
          :parecer="parecer"
          @disable-form-changed-parecer="
            $emit('disable-form-changed-parecer', $event)
          "
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BListGroup,
  BListGroupItem,
  BButton,
  BSpinner,
  BBadge,
} from "bootstrap-vue";
import DigitalParecerCardiologico from "./components/DigitalParecerCardiologico.vue";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BListGroup,
    BListGroupItem,
    BButton,
    BSpinner,
    BBadge,
    DigitalParecerCardiologico,
  },
  props: {
    atendimento: {
      type: Object,
      required: true,
    },
  },
  watch: {
    "atendimento.id": {
      handler() {
        this.loadPatientPareceres();
      },
      immediate: true,
    },
  },
  data() {
    return {
      showParecerCardiologicoForm: false,
      loading: false,
      pareceres: [],
      patientId: null,
      currentDate: this.formatDateToDDMMYYYY(new Date()),
      parecer: [],
    };
  },
  methods: {
    backToList() {
      this.loadPatientPareceres();
      this.showParecerCardiologicoForm = false;
    },
    /**
     * Formata a data para o formato dd-mm-yyyy
     */
    formatDateToDDMMYYYY(date) {
      if (!date) return "";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },

    /**
     * Transforma uma data no formato DD-MM-YYYY para DD/MM/YYYY
     */
    formatHyphenToSlash(dateString) {
      if (!dateString) return "";
      try {
        // Verifica se a data está no formato esperado DD-MM-YYYY
        if (
          typeof dateString !== "string" ||
          !/^\d{2}-\d{2}-\d{4}$/.test(dateString)
        ) {
          console.warn("Data não está no formato DD-MM-YYYY:", dateString);
          return dateString;
        }

        // Substitui os hifens por barras
        return dateString.replace(/-/g, "/");
      } catch (error) {
        console.error("Erro ao transformar formato da data:", error);
        return "";
      }
    },

    /**
     * Carrega os odontogramas do paciente usando o serviço getPatientById
     */
    async loadPatientPareceres() {
      this.patientId = this.atendimento.registro.id;
      if (!this.patientId) {
        return;
      }

      this.loading = true;
      try {
        // Usar o serviço diretamente ou através do Vuex
        const response = await this.$store.dispatch(
          "parecerCardiologicoModule/getPatientById",
          this.patientId
        );
        this.pareceres = (response || response.data || []).sort(
          (a, b) => b.id - a.id
        );
      } catch (error) {
        console.error("Erro ao carregar pareceres do paciente:", error);
        this.$bvToast.toast("Erro ao carregar pareceres do paciente", {
          title: "Erro",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },
    /**
     * Formata a data para exibição
     */
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    /**
     * Edita um parecer cardiológico existente
     */
    editParecerCardiologico(parecer) {
      this.loading = true;
      this.$store
        .dispatch("parecerCardiologicoModule/getById", parecer.id)
        .then((response) => {
          this.parecer = response;
          this.showParecerCardiologicoForm = true;
          this.loading = false;
        })
        .catch((error) => {
          console.error("Erro ao carregar parecer cardiológico:", error);
          this.loading = false;
        });
    },

    createParecerCardiologico() {
      this.$store
        .dispatch("parecerCardiologicoModule/generate", {
          atendimento: this.atendimento.id,
          dataParecer: this.currentDate,
          versao: "v2",
          observacao: "Criando um novo paracer",
          ativo: true,
        })
        .then((response) => {
          if (response.error) {
            this.$bvToast.toast(response.message, {
              title: "Erro",
              variant: "danger",
              solid: true,
            });
            return;
          }
          this.parecer = response.data;
          this.showParecerCardiologicoForm = true;
        });
    },

    /**
     * Atualiza um dente quando o componente DigitalOdontogram emite o evento
     */
    updateParecerCardiologico(updatedParecerCardiologico) {
      const index = this.pareceres.findIndex(
        (parecer) => parecer.id === updatedParecerCardiologico.id
      );
      if (index !== -1) {
        this.pareceres.splice(index, 1, updatedParecerCardiologico);
      }
    },
  },
};
</script>
